import { Button, Form, Input, Modal, Popconfirm, Select, Table, Tag, message } from "antd";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { serverUrl } from "../../nestserver";
import { useGetSchoolLocation } from "../../services/geofencing.service";
import MomentDatePicker, { TimePicker } from "../inputs/MomentDatePicker";
import {
  DAY_PERMISSION_KEY,
  DAY_PERMISSION_TYPES,
  dayPermissionLabel,
  useCreateDayPermission,
  useGetDayPermissions,
  useUpdateDayPermissionStatus,
  useWithdrawDayPermission,
} from "../../services/studentDayPermission.service";

const authHeaders = () => ({
  Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
});

const STATUS_COLOR = {
  pending: "gold",
  approved: "green",
  rejected: "red",
  withdraw: "default",
};

const errorText = (err, fallback) => {
  const e = err?.response?.data?.error?.errors;
  return (Array.isArray(e) ? e[0] : e) || fallback;
};

/**
 * Day Permission (late arrival / early pickup / flex).
 *
 * mode="requester": parent / student — request, view, withdraw.
 * mode="approver":  admin / instructor — approve / reject and log directly.
 * classesUrl: class list endpoint for the approver (admin `/classes/`,
 * instructor `/teachers/classes` or `/teachers/instructor-classes`).
 */
const DayPermission = ({ mode = "requester", classesUrl = "/classes/" }) => {
  const isApprover = mode === "approver";
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [modalOpen, setModalOpen] = useState(false);
  const [filterClass, setFilterClass] = useState();
  const [filterStatus, setFilterStatus] = useState();
  const [formClass, setFormClass] = useState();
  const [permissionFor, setPermissionFor] = useState("check_out");

  // School timings (same source the staff Permissions form uses). If they
  // aren't configured the time fields simply stay free.
  const schoolLocation = useGetSchoolLocation({ retry: false });
  const timings =
    schoolLocation.data?.check_in_time && schoolLocation.data?.check_out_time
      ? {
          start: moment(schoolLocation.data.check_in_time, "HH:mm"),
          end: moment(schoolLocation.data.check_out_time, "HH:mm"),
          startText: schoolLocation.data.check_in_time,
          endText: schoolLocation.data.check_out_time,
        }
      : null;
  const minutesOfDay = (t) => t.hours() * 60 + t.minutes();

  // Late arrival: From is school start. Early pickup: To is school end.
  useEffect(() => {
    if (!modalOpen || !timings) return;
    if (permissionFor === "check_in") form.setFieldsValue({ from_time: timings.start });
    else if (permissionFor === "check_out") form.setFieldsValue({ to_time: timings.end });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, permissionFor, schoolLocation.data]);

  const onTypeChange = (value) => {
    setPermissionFor(value);
    form.setFieldsValue({
      from_time: value === "check_in" && timings ? timings.start : undefined,
      to_time: value === "check_out" && timings ? timings.end : undefined,
    });
  };

  const params = {};
  if (filterClass) params.classId = filterClass;
  if (filterStatus) params.status = filterStatus;
  const list = useGetDayPermissions(params);

  // Approver: class list + the chosen class's students for "Log Permission"
  const classes = useQuery(
    ["day-permission-classes", classesUrl],
    async () => (await axios.get(serverUrl + classesUrl, { headers: authHeaders() })).data,
    { enabled: isApprover },
  );
  const classStudents = useQuery(
    ["day-permission-class-students", formClass],
    async () => {
      const res = await axios.get(
        `${serverUrl}/montessori/attendance/class/${formClass}?date=${moment().format("YYYY-MM-DD")}`,
        { headers: authHeaders() },
      );
      return (res.data?.result?.data || []).map((r) => r.student).filter(Boolean);
    },
    { enabled: isApprover && !!formClass },
  );

  // Parent: own children
  const children = useQuery(
    "day-permission-children",
    async () =>
      (await axios.get(`${serverUrl}/students/by-parent/`, { headers: authHeaders() })).data
        ?.result || [],
    { enabled: !isApprover },
  );
  const isParent = !isApprover && (children.data || []).length > 0;

  const refresh = () => queryClient.invalidateQueries(DAY_PERMISSION_KEY);

  const createMutation = useCreateDayPermission({
    onSuccess: () => {
      message.success("Day permission saved");
      setModalOpen(false);
      form.resetFields();
      setPermissionFor("check_out");
      setFormClass(undefined);
      refresh();
    },
    onError: (err) => message.error(errorText(err, "Failed to save")),
  });
  const statusMutation = useUpdateDayPermissionStatus({
    onSuccess: () => {
      message.success("Status updated");
      refresh();
    },
    onError: (err) => message.error(errorText(err, "Failed to update")),
  });
  const withdrawMutation = useWithdrawDayPermission({
    onSuccess: () => {
      message.success("Request withdrawn");
      refresh();
    },
    onError: (err) => message.error(errorText(err, "Failed to withdraw")),
  });

  const onFinish = (values) => {
    const day = values.date.format("YYYY-MM-DD");
    const withTime = (t) =>
      t ? moment(`${day} ${t.format("HH:mm")}`, "YYYY-MM-DD HH:mm").format() : undefined;
    createMutation.mutate({
      student: values.student,
      permission_for: values.permission_for,
      date: day,
      from_time: withTime(values.from_time),
      to_time: withTime(values.to_time),
      reason: values.reason,
    });
  };

  const columns = [
    {
      title: "Date",
      render: (r) => moment(r.date).utc().format("DD-MM-YYYY"),
    },
    {
      title: "Student",
      render: (r) => `${r.student?.first_name || ""} ${r.student?.last_name || ""}`,
    },
    { title: "Class", render: (r) => r.class?.name },
    { title: "Type", render: (r) => dayPermissionLabel(r.permission_for) },
    {
      title: "Time",
      render: (r) =>
        r.from_time || r.to_time
          ? `${r.from_time ? moment(r.from_time).format("hh:mm A") : "-"} to ${
              r.to_time ? moment(r.to_time).format("hh:mm A") : "-"
            }`
          : "-",
    },
    { title: "Reason", dataIndex: "reason" },
    {
      title: "Status",
      render: (r) => (
        <Tag color={STATUS_COLOR[r.status]} className="capitalize">
          {r.status}
        </Tag>
      ),
    },
    {
      title: "Action",
      render: (r) => {
        if (r.status !== "pending") return null;
        if (isApprover) {
          return (
            <div className="flex gap-2">
              <Popconfirm
                title="Approve this request?"
                onConfirm={() => statusMutation.mutate({ id: r._id, status: "approved" })}
              >
                <Button type="primary" size="small">
                  Approve
                </Button>
              </Popconfirm>
              <Popconfirm
                title="Reject this request?"
                onConfirm={() => statusMutation.mutate({ id: r._id, status: "rejected" })}
              >
                <Button size="small">Reject</Button>
              </Popconfirm>
            </div>
          );
        }
        return (
          <Popconfirm title="Withdraw this request?" onConfirm={() => withdrawMutation.mutate(r._id)}>
            <Button size="small">Withdraw</Button>
          </Popconfirm>
        );
      },
    },
  ];

  const studentOptions = isApprover
    ? classStudents.data || []
    : children.data || [];

  return (
    <div className="pt-8">
      <div className="bg-white p-10 rounded-2xl">
        <div className="flex justify-between items-center pb-6">
          <div className="text-2xl text-orange-500 font-semibold">Day Permission</div>
          <Button type="primary" size="large" onClick={() => setModalOpen(true)}>
            {isApprover ? "Log Permission" : "Request Permission"}
          </Button>
        </div>

        <div className="flex gap-4 pb-4">
          {isApprover && (
            <Select
              allowClear
              showSearch
              placeholder="All classes"
              style={{ width: 220 }}
              optionFilterProp="children"
              onChange={setFilterClass}
            >
              {(classes.data || []).map((c) => (
                <Select.Option key={c._id} value={c._id}>
                  {c.name}
                </Select.Option>
              ))}
            </Select>
          )}
          <Select allowClear placeholder="All statuses" style={{ width: 180 }} onChange={setFilterStatus}>
            {["pending", "approved", "rejected", "withdraw"].map((s) => (
              <Select.Option key={s} value={s}>
                <span className="capitalize">{s}</span>
              </Select.Option>
            ))}
          </Select>
        </div>

        <Table
          rowKey="_id"
          columns={columns}
          dataSource={list.data || []}
          loading={list.isLoading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: true }}
        />
      </div>

      <Modal
        title={isApprover ? "Log Day Permission" : "Request Day Permission"}
        visible={modalOpen}
        footer={null}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
          setPermissionFor("check_out");
        }}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{ permission_for: "check_out", date: moment() }}
        >
          {isApprover && (
            <Form.Item label="Class" required>
              <Select
                showSearch
                placeholder="Select class"
                optionFilterProp="children"
                onChange={(v) => {
                  setFormClass(v);
                  form.setFieldsValue({ student: undefined });
                }}
              >
                {(classes.data || []).map((c) => (
                  <Select.Option key={c._id} value={c._id}>
                    {c.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
          {(isApprover || isParent) && (
            <Form.Item
              label="Student"
              name="student"
              rules={[{ required: true, message: "Please select a student" }]}
            >
              <Select showSearch placeholder="Select student" optionFilterProp="children">
                {studentOptions.map((s) => (
                  <Select.Option key={s._id} value={s._id}>
                    {`${s.first_name} ${s.last_name}`}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item label="Type" name="permission_for" rules={[{ required: true }]}>
            <Select onChange={onTypeChange}>
              {DAY_PERMISSION_TYPES.map((t) => (
                <Select.Option key={t.value} value={t.value}>
                  {t.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Date" name="date" rules={[{ required: true, message: "Select a date" }]}>
            <MomentDatePicker
              format="DD MMM YYYY"
              allowClear={false}
              className="w-full"
              disabledDate={(d) => !isApprover && d && d.isBefore(moment().startOf("day"))}
            />
          </Form.Item>
          <div className="flex gap-4">
            <Form.Item
              label={
                permissionFor === "check_in"
                  ? "From (school start)"
                  : permissionFor === "check_out"
                    ? "Pickup time"
                    : "Leaving at"
              }
              name="from_time"
              className="flex-1"
              rules={[
                { required: true, message: "Select a time" },
                {
                  validator: (_, value) => {
                    if (value && timings && minutesOfDay(value) < minutesOfDay(timings.start)) {
                      return Promise.reject(
                        new Error(`Before school start (${timings.startText})`),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <TimePicker
                use12Hours
                format="hh:mm A"
                className="w-full"
                disabled={permissionFor === "check_in" && !!timings}
              />
            </Form.Item>
            <Form.Item
              label={
                permissionFor === "check_in"
                  ? "Arrival time"
                  : permissionFor === "check_out"
                    ? "To (school end)"
                    : "Returning by"
              }
              name="to_time"
              className="flex-1"
              dependencies={["from_time"]}
              rules={[
                { required: true, message: "Select a time" },
                ({ getFieldValue }) => ({
                  validator: (_, value) => {
                    const from = getFieldValue("from_time");
                    if (value && from && minutesOfDay(value) <= minutesOfDay(from)) {
                      return Promise.reject(new Error("Must be after the From time"));
                    }
                    if (value && timings && minutesOfDay(value) > minutesOfDay(timings.end)) {
                      return Promise.reject(
                        new Error(`After school end (${timings.endText})`),
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <TimePicker
                use12Hours
                format="hh:mm A"
                className="w-full"
                disabled={permissionFor === "check_out" && !!timings}
              />
            </Form.Item>
          </div>
          {timings && (
            <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
              <div className="text-sm font-bold text-blue-800">
                School Timings: {timings.startText} to {timings.endText}
              </div>
              <div className="mt-1 text-xs text-blue-600">
                {permissionFor === "check_in"
                  ? `From time is automatically set to school start (${timings.startText}); choose when the student will arrive.`
                  : permissionFor === "check_out"
                    ? `To time is automatically set to school end (${timings.endText}); choose when the student will be picked up.`
                    : "Choose when the student leaves and when they return."}
              </div>
            </div>
          )}
          <Form.Item
            label="Reason"
            name="reason"
            rules={[{ required: true, message: "Please enter a reason" }]}
          >
            <Input.TextArea rows={3} maxLength={300} placeholder="e.g. Taking child home after the exam" />
          </Form.Item>
          <div className="text-center">
            <Button type="primary" size="large" htmlType="submit" loading={createMutation.isLoading}>
              {isApprover ? "Save (auto-approved)" : "Submit"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default DayPermission;
